// components/food-logger/FoodSearch.jsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { usdaService } from '../../services/usdaService';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import { Loader2, Search } from "lucide-react";
import { Alert, AlertDescription } from "../ui/alert";
import { debounce } from 'lodash';
import { Badge } from "../ui/badge";

const FoodSearch = ({ onFoodSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNoResults, setShowNoResults] = useState(false);
  const [page, setPage] = useState(0);
  const [lastId, setLastId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Refs for infinite scroll
  const observerRef = useRef();
  const lastFoodElementRef = useRef();

  // Reset search state
  const resetSearch = () => {
    setSearchResults([]);
    setPage(0);
    setLastId(null);
    setHasMore(true);
    setShowNoResults(false);
    setError(null);
  };

  // Perform search
  const performSearch = async (query, isLoadingMore = false) => {
    try {
      if (!query.trim() || query.length < 2) {
        resetSearch();
        return;
      }

      if (!isLoadingMore) {
        setLoading(true);
        setError(null);
        setShowNoResults(false);
      }

      const data = await usdaService.searchFoods(query, {
        page,
        lastId,
        pageSize: 20
      });
      
      // Clear loading state immediately if no results
      if (!data.foods || data.foods.length === 0) {
        setSearchResults([]);
        setShowNoResults(true);
        setLoading(false);
        setIsLoadingMore(false);
        return;
      }

      if (isLoadingMore) {
        setSearchResults(prev => [...prev, ...data.foods]);
      } else {
        setSearchResults(data.foods);
      }
      setHasMore(data.pagination.hasMore);
      setLastId(data.pagination.lastId);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search foods. Please try again.');
      setSearchResults([]);
      setShowNoResults(true);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Debounce the search function
  const debouncedSearch = useCallback(
    debounce((query) => performSearch(query), 750),
    []
  );

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Reset search state on new query
    if (!query.trim() || query.length < 2) {
      resetSearch();
      return;
    }

    setPage(0);
    debouncedSearch(query);
  };

  // Handle food selection
  const handleFoodSelect = async (food) => {
    try {
      setLoading(true);
      const mappedFood = usdaService.mapUSDAToFood(food);
      onFoodSelect(mappedFood);
    } catch (err) {
      console.error('Error selecting food:', err);
      setError('Failed to get food details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format nutrition values for display
  const formatNutritionValue = (value) => {
    return value ? Math.round(value) : 0;
  };

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && searchQuery.trim().length >= 2) {
          setIsLoadingMore(true);
          setPage(prev => prev + 1);
          performSearch(searchQuery, true);
        }
      },
      { threshold: 0.5 }
    );
    
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, searchQuery]);

  // Observe last element
  useEffect(() => {
    if (lastFoodElementRef.current && observerRef.current) {
      observerRef.current.observe(lastFoodElementRef.current);
    }

    return () => {
      if (lastFoodElementRef.current && observerRef.current) {
        observerRef.current.unobserve(lastFoodElementRef.current);
      }
    };
  }, [searchResults]);

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search for a food (minimum 2 characters)..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-8"
            minLength={2}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Initial Loading State */}
      {loading && !isLoadingMore && (
        <div className="flex justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <ScrollArea className="h-[400px]">
          <div className="space-y-2">
            {searchResults.map((food, index) => {
              const isLastElement = index === searchResults.length - 1;
              return (
                <Card
                  key={`${food.fdcId}-${index}`}
                  ref={isLastElement ? lastFoodElementRef : null}
                  className="p-3 cursor-pointer hover:bg-accent transition-colors"
                  onClick={() => handleFoodSelect(food)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{food.description}</h4>
                        <Badge variant={food.source === 'local' ? "default" : "secondary"} className="text-xs">
                          {food.source === 'local' ? 'Database' : 'USDA API'}
                        </Badge>
                        {food.matchType === 'exact' && (
                          <Badge variant="outline" className="text-xs">
                            Exact Match
                          </Badge>
                        )}
                      </div>
                      {food.brandOwner && (
                        <p className="text-sm text-muted-foreground">
                          {food.brandOwner}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-medium">
                        {formatNutritionValue(food.calories)} cal
                      </p>
                      <p className="text-xs text-muted-foreground">
                        P: {formatNutritionValue(food.protein_g)}g
                        {' '}C: {formatNutritionValue(food.carbs_g)}g
                        {' '}F: {formatNutritionValue(food.fat_g)}g
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
            
            {/* Loading More Indicator */}
            {isLoadingMore && (
              <div className="flex justify-center p-4">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
          </div>
        </ScrollArea>
      )}

      {/* No Results Message */}
      {showNoResults && searchQuery.length >= 2 && !loading && (
        <div className="text-center p-4 text-muted-foreground">
          No foods found matching "{searchQuery}". Try a different search term.
        </div>
      )}

      {/* Initial State Message */}
      {!searchQuery && !loading && !error && (
        <div className="text-center p-4 text-muted-foreground">
          Start typing to search for foods...
        </div>
      )}

      {/* Minimum Characters Message */}
      {searchQuery.length === 1 && (
        <div className="text-center p-4 text-muted-foreground">
          Please enter at least 2 characters to search...
        </div>
      )}
    </div>
  );
};

export default FoodSearch;